import db from "@/plugins/dexie";
import geraData from "../geradata";

// Provider do checklist no modo dexie-first: lê dos espelhos (+ fila por cima) e grava na
// fila de envio. Os espelhos nunca são escritos aqui.

// Chave de idempotência do envio.
function geraChaveEnvio() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

// Último envio não confirmado do formulário (inclui conflito/falha).
async function envioPendente(idFormulario) {
  const envios = await db.formularioenvio
    .where({ fenidformulario: idFormulario })
    .filter((envio) => !envio.fendatanuvem)
    .toArray();

  if (!envios.length) return null;

  return envios.sort((a, b) => new Date(b.fendatagravacao) - new Date(a.fendatagravacao))[0];
}

// Mesma estrutura de GET formulario/completo/:id.
async function montaFormulario(idFormulario) {
  const formulario = await db.formulario.where({ foridnuvem: idFormulario }).first();
  if (!formulario) return {};

  const questoes = await db.formularioquestao
    .where({ foqidformularionuvem: idFormulario })
    .toArray();

  const identificacao = await db.formularioidentificacao
    .where({ foridformulario: idFormulario })
    .first();

  const linhas = identificacao
    ? await db.formularioresposta
      .where({ frsidformularioidentificacao: identificacao.foiidnuvem })
      .toArray()
    : [];
  // Sem duplicata por idnuvem (o delta pode reinserir uma linha gravada no envio).
  const respostas = [...new Map(linhas.map((linha) => [linha.frsidnuvem, linha])).values()];

  // O que está na fila vale mais que o espelho.
  const pendente = await envioPendente(idFormulario);
  const respostasPendentes = pendente?.fenpayload?.respostas ?? null;

  const respostaDaQuestao = (idQuestao) => {
    if (respostasPendentes) {
      const daFila = respostasPendentes.filter((item) => item.questao === idQuestao);
      if (daFila.length) return daFila.map((item) => ({ forresposta: item.resposta }));
      return [{ forresposta: null }];
    }

    const doEspelho = respostas.filter((item) => item.frsidformularioquestao === idQuestao);
    if (doEspelho.length) return doEspelho.map((item) => ({ forresposta: item.frsresposta }));
    return [{ forresposta: null }];
  };

  const montaQuestao = async (questao) => {
    const opcoes = await db.formularioquestaoopcao
      .where({ fqoidformularioquestaonuvem: questao.foqidnuvem })
      .toArray();

    const subquestoes = questoes
      .filter((sub) => sub.foqidformularioquestaonuvem === questao.foqidnuvem)
      .sort((a, b) => a.foqordem - b.foqordem);

    return {
      id: questao.foqidnuvem,
      foqnome: questao.foqnome,
      foqdescricao: questao.foqdescricao,
      foqtipo: questao.foqtipo,
      foqobrigatorio: questao.foqobrigatorio,
      foqordem: questao.foqordem,
      foqidformularionuvem: questao.foqidformularionuvem,
      foqidformularioquestaonuvem: questao.foqidformularioquestaonuvem,
      opcoes: opcoes
        .sort((a, b) => a.fqoordem - b.fqoordem)
        .map((opcao) => ({
          id: opcao.fqoidnuvem,
          fqodescricao: opcao.fqodescricao,
          fqoordem: opcao.fqoordem,
        })),
      subquestoes: await Promise.all(subquestoes.map(montaQuestao)),
      resposta: respostaDaQuestao(questao.foqidnuvem),
    };
  };

  const raiz = questoes
    .filter((questao) => !questao.foqidformularioquestaonuvem)
    .sort((a, b) => a.foqordem - b.foqordem);

  const dadosOrigem = formulario.fororigemdados ?? {};

  return {
    id: formulario.foridnuvem,
    fornome: formulario.fornome,
    fordescricao: formulario.fordescricao,
    fornomeimagem: formulario.fornomeimagem,
    fordatacadastro: formulario.fordatacadastro,
    // Conclusão na fila já conta como finalizado.
    fordatafinalizado: pendente?.fenpayload?.concluir
      ? pendente.fendatagravacao
      : formulario.fordatafinalizado,
    forexigecpf: formulario.forexigecpf,
    forexigenome: formulario.forexigenome,
    forcpfpadrao: pendente?.fenpayload?.user?.cpf?.value
      ?? identificacao?.foicpf
      ?? formulario.forcpfpadrao,
    fornomepadrao: pendente?.fenpayload?.user?.nome?.value
      ?? identificacao?.foinome
      ?? formulario.fornomepadrao,
    cabecalho: dadosOrigem.cabecalho ?? {},
    questoes: await Promise.all(raiz.map(montaQuestao)),
  };
}

// Grava na fila. Substitui o envio pendente que ainda não começou a subir; se já estiver
// em processo, enfileira outro.
async function salvar({
  formulario, user, respostas, useidusuario, concluir,
}) {
  const chaveenvio = geraChaveEnvio();
  const datagravacao = geraData();

  // Arquivos vão como Blob para outra store; no payload fica só o índice do anexo.
  const arquivos = [];
  const respostasSerializadas = respostas.map((item, indice) => {
    if (item.resposta instanceof File || item.resposta instanceof Blob) {
      arquivos.push({
        indice,
        questao: item.questao,
        blob: item.resposta,
        nome: item.resposta.name ?? `anexo-${indice}`,
        tipo: item.resposta.type ?? 'application/octet-stream',
      });
      return { questao: item.questao, resposta: null, anexo: indice };
    }

    return { questao: item.questao, resposta: item.resposta ?? null };
  });

  const payload = {
    respostas: respostasSerializadas,
    user: user ?? {},
    useidusuario: useidusuario ?? null,
    concluir: !!concluir,
  };

  return db.transaction('rw', db.formularioenvio, db.formularioenvioarquivo, async () => {
    const substituivel = await db.formularioenvio
      .where({ fenidformulario: formulario })
      .filter((envio) => !envio.fendatanuvem && !envio.fenemprocesso)
      .toArray();

    // eslint-disable-next-line no-restricted-syntax
    for (const antigo of substituivel) {
      await db.formularioenvioarquivo.where({ faeidenvio: antigo.id }).delete();
      await db.formularioenvio.delete(antigo.id);
    }

    const idEnvio = await db.formularioenvio.add({
      fenidformulario: formulario,
      fenchaveenvio: chaveenvio,
      fenpayload: payload,
      fendatagravacao: datagravacao,
      fendatanuvem: null,
      fenemprocesso: false,
      fendataprocesso: null,
      fententativas: 0,
      fenproximatentativa: null,
      fenfalhapermanente: false,
      fenconflito: false,
      fenerro: null,
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const arquivo of arquivos) {
      await db.formularioenvioarquivo.add({
        faeidenvio: idEnvio,
        faequestao: arquivo.questao,
        faeindice: arquivo.indice,
        faeblob: arquivo.blob,
        faenome: arquivo.nome,
        faetipo: arquivo.tipo,
      });
    }

    return { id: idEnvio, chaveenvio };
  });
}

// baseURL só monta URL de capa/anexo já existentes no servidor.
export default function criaChecklistOffline({ baseURL } = {}) {
  return {
    baseURL,
    find: (id) => montaFormulario(id),
    salvar,
  };
}

export { montaFormulario, envioPendente, geraChaveEnvio };
